/**
 * Monkey Worlds — client constants. Display figures mirror server/src/constants/monkeyWorlds
 * (server stays the source of truth for what's actually awarded). Colors reuse the shared Warmup
 * palette; world accents intentionally match sportTint() in CompetitionCard.tsx (do not refactor
 * that function — the hexes are aligned by convention, see specs/monkey-worlds/spec.md).
 *
 * Copy guard: user-facing strings never name a real league/team/player — the NFL-sport world is
 * "Gridiron World" everywhere a user can see it.
 */

import { WARMUP_COLORS } from './warmup.constants';

export const MONKEY_WORLDS_GAME_KEY = 'MONKEY_WORLDS';

/** Display figures (server authoritative). Mirror server/src/constants/monkeyWorlds. */
export const MONKEY_WORLDS_WORLD_COUNT = 4;
export const MONKEY_WORLDS_LEVELS_PER_WORLD = 6;
export const MONKEY_WORLDS_COINS_PER_LEVEL = 2;
export const MONKEY_WORLDS_DAILY_COIN_CAP = 48;

/** The four sport worlds. Order is canonical: index = world id (0-3) used across bridge, server
 * progress packing, and level data. Internal `sport` ids are never shown to users. */
export interface MonkeyWorld {
  /** Internal sport id (matches competition sport strings). Never user-facing. */
  sport: 'FOOTBALL' | 'BASKETBALL' | 'BASEBALL' | 'NFL';
  /** User-facing world name (copy-guard safe). */
  name: string;
  accent: string;
  skyGradient: [string, string];
  tileTint: string;
  /**
   * The world's sport shot. Movement (jump/slide) is identical everywhere; this is what makes
   * each world *play* its sport — you score it into that sport's goal to finish a level.
   */
  action: {
    type: 'kick' | 'shoot' | 'bat' | 'throw';
    /** Button label inside the canvas HUD. */
    label: string;
    cooldownTicks: number;
    /**
     * Launch velocity (tiles/tick) and the ball's gravity for this sport — together they set the
     * arc, and therefore where you have to stand to make the shot. `g` is per-sport because a
     * jumper has to drop through a rim quickly while a drive stays flat.
     */
    shot: { vx: number; vy: number; g: number };
  };
}

export const MONKEY_WORLDS: readonly MonkeyWorld[] = [
  {
    sport: 'FOOTBALL',
    name: 'Football World',
    accent: WARMUP_COLORS.green, // #6EE7B7
    skyGradient: ['#0F0A1A', '#0E2A22'],
    tileTint: '#2F5A44',
    action: { type: 'kick', label: 'KICK', cooldownTicks: 20, shot: { vx: 0.3, vy: -0.08, g: 0.0028 } },
  },
  {
    sport: 'BASKETBALL',
    name: 'Hoops World',
    accent: WARMUP_COLORS.amber, // #FCD34D
    skyGradient: ['#0F0A1A', '#2A1E0E'],
    tileTint: '#5A452F',
    action: { type: 'shoot', label: 'SHOOT', cooldownTicks: 20, shot: { vx: 0.1, vy: -0.21, g: 0.005 } },
  },
  {
    sport: 'BASEBALL',
    name: 'Diamond World',
    accent: WARMUP_COLORS.danger, // #F87171
    skyGradient: ['#0F0A1A', '#2A0E14'],
    tileTint: '#5A2F38',
    action: { type: 'bat', label: 'BAT', cooldownTicks: 20, shot: { vx: 0.32, vy: -0.12, g: 0.0026 } },
  },
  {
    sport: 'NFL',
    name: 'Gridiron World',
    accent: WARMUP_COLORS.purple, // #A78BFA
    skyGradient: ['#0F0A1A', '#1D1430'],
    tileTint: '#3D2F5A',
    action: { type: 'throw', label: 'THROW', cooldownTicks: 22, shot: { vx: 0.28, vy: -0.14, g: 0.0028 } },
  },
] as const;

/** Card accent for the hub tile. */
export const MONKEY_WORLDS_ACCENT = WARMUP_COLORS.purple;
export const MONKEY_WORLDS_CARD_GRADIENT: [string, string] = ['#1D1430', '#140D22'];

/** Inline SVG card art — the monkey mid-jump over four sport orbs on a night skyline. */
export const MONKEY_WORLDS_CARD_ART_URI = `data:image/svg+xml,${encodeURIComponent(`<svg viewBox="0 0 400 280" xmlns="http://www.w3.org/2000/svg"><defs><linearGradient id="sky" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#0F0A1A"/><stop offset="100%" stop-color="#2B1B4D"/></linearGradient><linearGradient id="hill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#3D2F5A"/><stop offset="100%" stop-color="#241A38"/></linearGradient></defs><rect width="400" height="280" fill="url(#sky)" rx="16"/><circle cx="330" cy="55" r="26" fill="#FDE68A" opacity=".9"/><circle cx="322" cy="48" r="26" fill="url(#sky)"/><g opacity=".55"><circle cx="45" cy="40" r="2" fill="#fff"/><circle cx="120" cy="70" r="1.6" fill="#fff"/><circle cx="205" cy="35" r="2" fill="#fff"/><circle cx="280" cy="90" r="1.5" fill="#fff"/><circle cx="360" cy="120" r="1.8" fill="#fff"/></g><path d="M0 220 Q100 170 200 210 T400 200 V280 H0 Z" fill="url(#hill)"/><g transform="translate(48,196)"><rect x="-16" y="-14" width="32" height="28" rx="6" fill="#A78BFA"/><rect x="-16" y="-14" width="32" height="8" rx="4" fill="#C4B5FD"/></g><g transform="translate(120,178)"><rect x="-16" y="-14" width="32" height="28" rx="6" fill="#6EE7B7"/><circle r="10" fill="#0E2A22"/><circle r="9" fill="#fff"/><path d="M0-9 L2.6-3.6 8.5-3 4 1 5.3 6.9 0 4 -5.3 6.9 -4 1 -8.5-3 -2.6-3.6 Z" fill="#1F2937" transform="scale(.62)"/></g><g transform="translate(196,196)"><circle r="14" fill="#FCD34D"/><path d="M-14 0 H14 M0 -14 V14 M-10-10 Q0 0 -10 10 M10-10 Q0 0 10 10" stroke="#92400E" stroke-width="2" fill="none"/></g><g transform="translate(272,182)"><circle r="13" fill="#F8FAFC"/><path d="M-9-9 Q-2 0 -9 9 M9-9 Q2 0 9 9" stroke="#F87171" stroke-width="2" fill="none"/></g><g transform="translate(344,198)"><ellipse rx="16" ry="11" fill="#7C4A21"/><path d="M-8 0 H8 M-5-3 V3 M0-4 V4 M5-3 V3" stroke="#F8FAFC" stroke-width="1.8"/></g><g transform="translate(200,108)"><ellipse cx="0" cy="34" rx="26" ry="6" fill="rgba(0,0,0,.35)"/><circle r="30" fill="#8B5A2B"/><circle r="21" cy="6" fill="#C89F7B"/><circle cx="-13" cy="-6" r="6.5" fill="#C89F7B"/><circle cx="13" cy="-6" r="6.5" fill="#C89F7B"/><circle cx="-8" cy="-4" r="3.4" fill="#1F2937"/><circle cx="8" cy="-4" r="3.4" fill="#1F2937"/><circle cx="-6.8" cy="-5.2" r="1.2" fill="#fff"/><circle cx="9.2" cy="-5.2" r="1.2" fill="#fff"/><path d="M-7 12 Q0 19 7 12" stroke="#1F2937" stroke-width="2.4" fill="none" stroke-linecap="round"/><path d="M26 8 Q46 2 44 -16" stroke="#8B5A2B" stroke-width="7" fill="none" stroke-linecap="round"/></g></svg>`)}`;

/** Colors used by RN-side overlays. */
export const MONKEY_WORLDS_COLORS = {
  accent: MONKEY_WORLDS_ACCENT,
  star: WARMUP_COLORS.amber,
  bg: WARMUP_COLORS.bg,
} as const;

export const MONKEY_WORLDS_TEST_IDS = {
  view: 'monkey-worlds-view',
  loading: 'monkey-worlds-loading',
  error: 'monkey-worlds-error',
  retry: 'monkey-worlds-retry',
  back: 'monkey-worlds-back',
  capBanner: 'monkey-worlds-cap-banner',
} as const;

/** Messages posted from the WebView game runtime to the RN host over the bridge. */
export type MonkeyWorldsBridgeMessage =
  | { type: 'ready' }
  | {
      type: 'level-clear';
      world: number;
      slot: number;
      stars: 1 | 2 | 3;
      timeTicks: number;
      coins: number;
    }
  | { type: 'level-failed'; world: number; slot: number }
  | { type: 'world-complete'; world: number }
  /** The in-canvas speaker was tapped; the host mirrors it into the app-wide mute (AC-19). */
  | { type: 'audio'; muted: boolean }
  | { type: 'load-error' };

/** Boot payload injected into the runtime via the applyStart intent. */
export interface MonkeyWorldsStartPayload {
  /** Packed per-world furthest-slot progress (byte per world, world 0 = LSB). */
  worldProgress: number;
  /** Star map keyed "world:slot" → 1|2|3 (client-persisted, cosmetic). */
  stars: Record<string, 1 | 2 | 3>;
  /** Top safe-area inset in px so the canvas HUD clears the notch. */
  insetTop: number;
  /** Boot mute state, mirrored from the app-wide setting (AC-19). */
  muted: boolean;
}

/** Payload for the applyAudio intent — the host pushing a mute change into the runtime. */
export interface MonkeyWorldsAudioPayload {
  muted: boolean;
}
